import { FC } from 'react';
import { cn } from '@/shared/lib';
import type { SankeyNodeItem } from './sankeyData';

/** Отступ подписи от прямоугольника узла в единицах диаграммы. */
const LABEL_GAP = 8;

/** Наименьшая высота области подписи: у тонкого узла подпись всё равно читается в две строки. */
const LABEL_MIN_HEIGHT = 40;

type Props = {
  /** Координаты и размеры узла, посчитанные диаграммой */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Ширина полосы, отведённой под подпись сбоку от узла */
  labelWidth: number;
  node: SankeyNodeItem;
  /** Изоляция другого узла: этот показан приглушённо */
  isDimmed: boolean;
  onClick: () => void;
};

/**
 * Узел диаграммы переходов: столбик понятия и его подпись сбоку — слева у возражений,
 * справа у аргументов. Подпись переносится по словам и показывается целиком: усечённое
 * название понятия не отличить от соседнего с тем же началом.
 */
export const SankeyNodeShape: FC<Props> = ({ x, y, width, height, labelWidth, node, isDimmed, onClick }) => {
  const labelHeight = Math.max(height, LABEL_MIN_HEIGHT);
  const labelY = y + height / 2 - labelHeight / 2;
  const isObjection = node.side === 'objection';
  const labelX = isObjection ? x - labelWidth : x + width + LABEL_GAP;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={2}
        fill="currentColor"
        className="text-foreground cursor-pointer"
        fillOpacity={isDimmed ? 0.25 : 0.75}
        role="button"
        tabIndex={-1}
        onClick={onClick}
      />
      <foreignObject
        x={labelX}
        y={labelY}
        width={Math.max(labelWidth - LABEL_GAP, 0)}
        height={labelHeight}
        style={{ overflow: 'visible' }}
      >
        <div
          className={cn(
            'flex h-full w-full items-center',
            isObjection ? 'justify-end pr-1 text-right' : 'justify-start pl-1 text-left',
          )}
        >
          <span
            className={cn('text-xs leading-tight break-words', isDimmed ? 'text-muted-foreground' : 'text-foreground')}
          >
            {node.name}
          </span>
        </div>
      </foreignObject>
    </g>
  );
};
